import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<span class=vcr__warning data-testid=vcr-warning>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=vcr data-testid=vcr-bar><div class=vcr__transport><button type=button class=vcr__button data-testid=vcr-rewind>⏮ REWIND</button><button type=button class=vcr__button data-testid=vcr-stop>⏹ STOP</button><button type=button class="vcr__button vcr__button--primary"data-testid=vcr-play></button><span class=vcr__counter data-testid=vcr-counter>TIME </span><span class="vcr__counter vcr__counter--dim">/ </span></div><input type=range class=vcr__slider data-testid=vcr-slider min=0 max=1000 step=1 aria-label="Location slider"><div class=vcr__row><div class=vcr__group role=group aria-label="Playback speed"><span class=vcr__label>SPEED</span></div><div class=vcr__group role=group aria-label=Camera><span class=vcr__label>VIEW</span><button type=button class=vcr__chip data-testid=vcr-switch>SWITCH</button><button type=button class=vcr__chip>YOURS</button></div></div><div class="vcr__row vcr__row--foot"><span class=vcr__tape data-testid=vcr-tape-name>TAPE NAME · </span><span class=vcr__status></span><span class=vcr__spacer></span><button type=button class=vcr__button data-testid=vcr-tapes>TAPES</button><button type=button class=vcr__button data-testid=vcr-exit>EXIT`),
  _tmpl$3 = /*#__PURE__*/_$template(`<button type=button class=vcr__chip>`);
import { For, Show, onCleanup, onMount } from "solid-js";
import { locationCounter, playbackFraction, rateLabel, tickForFraction, PLAYBACK_RATES } from "./playback-speed.ts";
/** Breathing room above the bar's own measured height, so the HUD's bottom
 * row doesn't sit flush against the bar's top edge. */
const VCR_CLEARANCE_BUFFER_PX = 8;

/**
 * The Mission Record control panel.
 *
 * The original's own layout, in its own words: `Playback Controls` (a transport
 * that "operate[s] like your everyday VCR controls"), a `Location Counter` that
 * "keeps a running mark of your playback position", a `Location Slider` that
 * doubles as a scrub, an `Active Camera Controls` block split `YOURS` /
 * `OTHERS`, and `EXIT` / `TAPES` along the bottom. The captured DOSBox screen
 * (docs/game/ui-reference/vcr-playback.png) shows the same set with a
 * `TIME 00:00:00` readout and a `TAPE NAME` field.
 *
 * ## Where this deliberately departs
 *
 * Two of the original's limits were consequences of re-simulating on 1990
 * hardware, not decisions, and are not inherited:
 *
 * - **Rewind is not all-or-nothing.** The manual is explicit — "Rewind will only
 *   rewind the tape to the beginning... It's all or nothing" and the slider
 *   "cannot be moved backward". Our seek index keeps a state every ten seconds,
 *   so scrubbing backwards costs a few milliseconds (`replay-play.ts`).
 * - **There is a speed control.** The original had Play and Fast-forward, where
 *   fast-forward was a *seek* with an on-screen countdown. A rate ladder is more
 *   useful and, at 8x, cheaper than the countdown was.
 *
 * `REWIND` is kept as a button anyway, doing what it always did — go to the
 * start — because it is the one transport action a pilot reaches for by name.
 */
export function VcrBar(props) {
  const fraction = () => playbackFraction(props.position.tick, props.position.ticks);
  let rootRef;

  // The bar's rows wrap on narrow viewports and grow with data (a VIEW chip
  // per viewable aircraft, a free-text warning line), so its rendered height
  // is not a constant the CSS clearance rule (`.flight:has(.vcr) .hud`) can
  // hardcode. Measure it and publish it as a custom property on `.flight`
  // (the nearest ancestor shared with `.hud`) instead — see app.css.
  onMount(() => {
    const flight = rootRef?.closest(".flight");
    if (!rootRef || !flight) return;
    const publish = () => {
      const height = rootRef?.getBoundingClientRect().height ?? 0;
      if (height > 0) flight.style.setProperty("--vcr-height", `${Math.ceil(height) + VCR_CLEARANCE_BUFFER_PX}px`);
    };
    const observer = new ResizeObserver(publish);
    observer.observe(rootRef);
    publish();
    onCleanup(() => {
      observer.disconnect();
      flight.style.removeProperty("--vcr-height");
    });
  });
  return (() => {
    var _el$ = _tmpl$2(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.firstChild,
      _el$4 = _el$3.nextSibling,
      _el$5 = _el$4.nextSibling,
      _el$6 = _el$5.nextSibling,
      _el$7 = _el$6.firstChild,
      _el$8 = _el$6.nextSibling,
      _el$9 = _el$8.firstChild,
      _el$0 = _el$2.nextSibling,
      _el$1 = _el$0.nextSibling,
      _el$10 = _el$1.firstChild,
      _el$11 = _el$10.firstChild,
      _el$12 = _el$10.nextSibling,
      _el$13 = _el$12.firstChild,
      _el$14 = _el$13.nextSibling,
      _el$15 = _el$14.nextSibling,
      _el$16 = _el$1.nextSibling,
      _el$17 = _el$16.firstChild,
      _el$18 = _el$17.firstChild,
      _el$19 = _el$17.nextSibling,
      _el$21 = _el$19.nextSibling,
      _el$22 = _el$21.nextSibling,
      _el$23 = _el$22.nextSibling;
    var _ref$ = rootRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$) : rootRef = _el$;
    _el$3.$$click = () => props.onRewind();
    _el$4.$$click = () => props.onStop();
    _el$5.$$click = () => props.onPlayPause();
    _$insert(_el$5, () => props.position.playing ? "⏸ PAUSE" : "▶ PLAY");
    _$insert(_el$6, () => locationCounter(props.position.tick), null);
    _$insert(_el$8, () => locationCounter(props.position.ticks), null);
    _el$0.$$input = event => props.onSeek(tickForFraction(Number(event.currentTarget.value) / 1000, props.position.ticks));
    _$insert(_el$10, _$createComponent(For, {
      each: PLAYBACK_RATES,
      children: rate => (() => {
        var _el$24 = _tmpl$3();
        _el$24.$$click = () => props.onRate(rate);
        _$insert(_el$24, () => rateLabel(rate));
        _$effect(() => _el$24.classList.toggle("vcr__chip--active", !!(props.rate === rate)));
        return _el$24;
      })()
    }), null);
    _el$14.$$click = () => props.onSwitchView();
    _el$15.$$click = () => props.onTarget(null);
    _$insert(_el$12, _$createComponent(For, {
      get each() {
        return props.viewable;
      },
      children: entry => (() => {
        var _el$25 = _tmpl$3();
        _el$25.$$click = () => props.onTarget(entry.id);
        _$insert(_el$25, () => entry.label);
        _$effect(() => _el$25.classList.toggle("vcr__chip--active", !!(props.target === entry.id)));
        return _el$25;
      })()
    }), null);
    _$insert(_el$17, () => props.tapeLabel, null);
    _$insert(_el$19, () => props.cameraLabel);
    _$insert(_el$16, _$createComponent(Show, {
      get when() {
        return props.warning;
      },
      get children() {
        var _el$20 = _tmpl$();
        _$insert(_el$20, () => props.warning);
        return _el$20;
      }
    }), _el$21);
    _el$22.$$click = () => props.onTapes();
    _el$23.$$click = () => props.onExit();
    _$effect(() => _el$15.classList.toggle("vcr__chip--active", !!(props.target === null)));
    _$effect(() => _el$0.value = Math.round(fraction() * 1000));
    return _el$;
  })();
}
_$delegateEvents(["click", "input"]);